(function (root, factory) {
  const api = factory(root);
  const app = root.App = root.App || {};
  app.locale = api;
  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }
})(typeof globalThis !== 'undefined' ? globalThis : this, function (root) {
  'use strict';

  const SUPPORTED_LOCALES = ['en', 'cs'];
  const DEFAULT_LOCALE = 'en';
  const COOKIE_NAME = 'ui_locale';
  const STORAGE_KEY_PREFIX = 'ui-locale';

  function toCleanString(value) {
    return typeof value === 'string' ? value.trim() : '';
  }

  function normalizeLocale(value) {
    const raw = toCleanString(value).toLowerCase();
    if (!raw) return DEFAULT_LOCALE;
    if (raw === 'cs' || raw === 'cz' || raw.startsWith('cs-')) return 'cs';
    if (raw === 'en' || raw.startsWith('en-')) return 'en';
    return DEFAULT_LOCALE;
  }

  function buildStorageKey(identity) {
    const workspaceId = toCleanString(identity && identity.workspaceId);
    const userId = toCleanString(identity && identity.userId);
    if (!workspaceId || !userId) {
      return STORAGE_KEY_PREFIX;
    }
    return `${STORAGE_KEY_PREFIX}:${workspaceId}:${userId}`;
  }

  function getStorageHandle(explicitStorage) {
    if (explicitStorage && typeof explicitStorage.getItem === 'function') {
      return explicitStorage;
    }
    try {
      if (root && root.localStorage && typeof root.localStorage.getItem === 'function') {
        return root.localStorage;
      }
    } catch (_) {
      return null;
    }
    return null;
  }

  function readStorageValue(storage, key) {
    if (!storage || !key) return '';
    try {
      return toCleanString(storage.getItem(key));
    } catch (_) {
      return '';
    }
  }

  function writeStorageValue(storage, key, value) {
    if (!storage || !key) return;
    try {
      storage.setItem(key, value);
    } catch (_) {
      // Ignore persistence issues; locale will still be applied in-memory.
    }
  }

  function readCookie(cookieSource, name) {
    const cookieText = typeof cookieSource === 'string'
      ? cookieSource
      : toCleanString(cookieSource && cookieSource.cookie);
    if (!cookieText || !name) return '';
    const entries = cookieText.split(';');
    for (const entry of entries) {
      const trimmed = entry.trim();
      if (!trimmed) continue;
      const separator = trimmed.indexOf('=');
      if (separator <= 0) continue;
      const key = trimmed.slice(0, separator).trim();
      if (key !== name) continue;
      const value = trimmed.slice(separator + 1).trim();
      try {
        return decodeURIComponent(value);
      } catch (_) {
        return value;
      }
    }
    return '';
  }

  function writeCookie(doc, name, value) {
    if (!doc || typeof doc.cookie !== 'string' && typeof doc.cookie !== 'undefined') {
      return;
    }
    try {
      doc.cookie = `${name}=${encodeURIComponent(value)}; path=/; SameSite=Lax`;
    } catch (_) {
      // Ignore cookie persistence errors.
    }
  }

  function persistServerLocale(locale) {
    if (!root || typeof root.fetch !== 'function') return;
    try {
      void root.fetch('/auth/locale', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ language: locale }),
      }).catch(() => {});
    } catch (_) {
      // Login pages are unauthenticated; the login request persists the cookie locale.
    }
  }

  function resolveLocale(options = {}) {
    const identity = options.identity || {};
    const storage = getStorageHandle(options.storage);
    const storageKey = buildStorageKey(identity);
    const storedSpecific = normalizeLocale(readStorageValue(storage, storageKey));
    if (readStorageValue(storage, storageKey)) {
      return storedSpecific;
    }
    const storedGeneric = normalizeLocale(readStorageValue(storage, STORAGE_KEY_PREFIX));
    if (readStorageValue(storage, STORAGE_KEY_PREFIX)) {
      return storedGeneric;
    }
    const cookieValue = normalizeLocale(readCookie(options.cookieSource || (root && root.document), COOKIE_NAME));
    if (readCookie(options.cookieSource || (root && root.document), COOKIE_NAME)) {
      return cookieValue;
    }
    const datasetLocale = toCleanString(options.datasetLocale);
    if (datasetLocale) {
      return normalizeLocale(datasetLocale);
    }
    return normalizeLocale(options.fallbackLocale || DEFAULT_LOCALE);
  }

  function persistLocale(locale, options = {}) {
    const normalized = normalizeLocale(locale);
    const storage = getStorageHandle(options.storage);
    const identity = options.identity || {};
    writeStorageValue(storage, buildStorageKey(identity), normalized);
    writeStorageValue(storage, STORAGE_KEY_PREFIX, normalized);
    writeCookie(options.doc || (root && root.document), COOKIE_NAME, normalized);
    persistServerLocale(normalized);
    return normalized;
  }

  return {
    SUPPORTED_LOCALES,
    DEFAULT_LOCALE,
    COOKIE_NAME,
    STORAGE_KEY_PREFIX,
    normalizeLocale,
    buildStorageKey,
    readCookie,
    resolveLocale,
    persistLocale,
    syncServerLocale: persistServerLocale,
  };
});
